/**
 * Standards API Service
 *
 * API service for browsing and fetching standards from .equilateral-standards/
 *
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, type APIResponse } from './utils';
import { API_ENDPOINTS } from './endpoints';

export interface StandardCategory {
  name: string;
  displayName: string;
  description: string;
  count: number;
  path: string;
}

export interface StandardFile {
  filename: string;
  displayName: string;
  category: string;
  path: string;
  size: number;
  lastModified: string;
}

export interface StandardContent {
  filename: string;
  content: string;
  category: string;
  path: string;
  metadata?: {
    title?: string;
    description?: string;
    version?: string;
    lastUpdated?: string;
  };
}

export interface StandardSearchResult {
  filename: string;
  category: string;
  path: string;
  excerpt: string;
  relevance: number;
}

/**
 * List all available standard categories
 */
export async function listStandardCategories(): Promise<APIResponse<StandardCategory[]>> {
  try {
    const response = await Make_Authorized_API_Call<{ Records: StandardCategory[] }>(
      API_ENDPOINTS.STANDARDS.CATEGORIES,
      'GET'
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.Records,
        message: 'Categories loaded successfully'
      };
    }

    return {
      success: false,
      data: [] as StandardCategory[],
      message: response.message || 'Failed to load categories'
    };
  } catch (error) {
    console.error('Error loading standard categories:', error);
    return {
      success: false,
      data: [] as StandardCategory[],
      message: 'An error occurred while loading categories'
    };
  }
}

/**
 * List all standards in a specific category
 */
export async function listStandardsInCategory(category: string): Promise<APIResponse<StandardFile[]>> {
  try {
    const response = await Make_Authorized_API_Call<{ Records: StandardFile[] }>(
      API_ENDPOINTS.STANDARDS.LIST,
      'GET',
      undefined,
      { params: { category } }
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.Records,
        message: 'Standards loaded successfully'
      };
    }

    return {
      success: false,
      data: [] as StandardFile[],
      message: response.message || 'Failed to load standards'
    };
  } catch (error) {
    console.error('Error loading standards:', error);
    return {
      success: false,
      data: [] as StandardFile[],
      message: 'An error occurred while loading standards'
    };
  }
}

/**
 * Get content of a specific standard
 */
export async function getStandardContent(path: string): Promise<APIResponse<StandardContent>> {
  try {
    const response = await Make_Authorized_API_Call<StandardContent>(
      API_ENDPOINTS.STANDARDS.CONTENT,
      'GET',
      undefined,
      { params: { path } }
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: 'Standard content loaded successfully'
      };
    }

    return {
      success: false,
      data: {} as StandardContent,
      message: response.message || 'Failed to load standard content'
    };
  } catch (error) {
    console.error('Error loading standard content:', error);
    return {
      success: false,
      data: {} as StandardContent,
      message: 'An error occurred while loading standard content'
    };
  }
}

/**
 * Search standards by query
 */
export async function searchStandards(query: string): Promise<APIResponse<StandardSearchResult[]>> {
  try {
    const response = await Make_Authorized_API_Call<{ Records: StandardSearchResult[] }>(
      API_ENDPOINTS.STANDARDS.SEARCH,
      'GET',
      undefined,
      { params: { q: query } }
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.Records,
        message: 'Search completed successfully'
      };
    }

    return {
      success: false,
      data: [] as StandardSearchResult[],
      message: response.message || 'Search failed'
    };
  } catch (error) {
    console.error('Error searching standards:', error);
    return {
      success: false,
      data: [] as StandardSearchResult[],
      message: 'An error occurred while searching'
    };
  }
}

/**
 * Get standard by filename (convenience method)
 */
export async function getStandardByFilename(
  category: string,
  filename: string
): Promise<APIResponse<StandardContent>> {
  const path = `${category}/${filename}`;
  return getStandardContent(path);
}

/**
 * Get all standards (for browsing all at once)
 */
export async function listAllStandards(): Promise<APIResponse<StandardFile[]>> {
  try {
    const response = await Make_Authorized_API_Call<{ Records: StandardFile[] }>(
      API_ENDPOINTS.STANDARDS.LIST,
      'GET'
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.Records,
        message: 'All standards loaded successfully'
      };
    }

    return {
      success: false,
      data: [] as StandardFile[],
      message: response.message || 'Failed to load standards'
    };
  } catch (error) {
    console.error('Error loading all standards:', error);
    return {
      success: false,
      data: [] as StandardFile[],
      message: 'An error occurred while loading standards'
    };
  }
}
